// G03 Hidden Revenue, G04 Revenue Bridge, G12 Concentration Analyzer.
import { useMemo } from "react";
import { hiddenRevenue, revenueBridge, concentration, inr, pct } from "./engines";
import { Field, ResultRow, Note, ToolSection, Waterfall, useNumbers } from "./ui";
import { useState } from "react";

export function ToolG03() {
  const [scenario, setScenario] = useState("base");
  const [v, set] = useNumbers({ overlapPct: 15 });
  const [levers, setLevers] = useNumbers({
    crosssell_e: 2000, crosssell_a: 10, crosssell_v: 15000,
    sku_e: 1500, sku_a: 8, sku_v: 9000,
    freq_e: 2500, freq_a: 12, freq_v: 6000,
    conv_e: 800, conv_a: 10, conv_v: 25000,
  });
  const r = useMemo(() => hiddenRevenue({
    overlapPct: v.overlapPct,
    scenario,
    levers: [
      { label: "Cross-sell to active customers", eligible: levers.crosssell_e, adoptionPct: levers.crosssell_a, valuePerAdopter: levers.crosssell_v },
      { label: "Missing SKUs in buying accounts", eligible: levers.sku_e, adoptionPct: levers.sku_a, valuePerAdopter: levers.sku_v },
      { label: "Purchase frequency uplift", eligible: levers.freq_e, adoptionPct: levers.freq_a, valuePerAdopter: levers.freq_v },
      { label: "Convert eligible non-buyers", eligible: levers.conv_e, adoptionPct: levers.conv_a, valuePerAdopter: levers.conv_v },
    ],
  }), [levers, v.overlapPct, scenario]);

  const LeverRow = ({ k, label }) => (
    <div className="border border-zinc-200 bg-white p-4">
      <div className="text-sm font-medium">{label}</div>
      <div className="mt-2 grid grid-cols-3 gap-3">
        <Field label="Eligible" value={levers[`${k}_e`]} onChange={set(`${k}_e`)} testid={`g03-${k}-e`} />
        <Field label="Adoption %" value={levers[`${k}_a`]} onChange={set(`${k}_a`)} testid={`g03-${k}-a`} />
        <Field label="₹ / adopter" value={levers[`${k}_v`]} onChange={set(`${k}_v`)} testid={`g03-${k}-v`} />
      </div>
    </div>
  );

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-4">
        <LeverRow k="crosssell" label="Cross-sell to active customers" />
        <LeverRow k="sku" label="Missing SKUs in buying accounts" />
        <LeverRow k="freq" label="Purchase frequency uplift" />
        <LeverRow k="conv" label="Convert eligible non-buyers" />
        <Field label="Customer overlap between levers" hint="The same customer counted in two levers is adjusted down." suffix="%" value={v.overlapPct} onChange={set("overlapPct")} testid="g03-overlap" />
      </div>
      <div>
        <div className="flex gap-2 mb-4" data-testid="g03-scenarios">
          {["conservative", "base", "stretch"].map((s) => (
            <button key={s} type="button" onClick={() => setScenario(s)}
              className={`h-9 px-4 text-sm font-medium border transition-colors ${scenario === s ? "bg-black text-white border-black" : "border-zinc-300 hover:border-black"}`}
              data-testid={`g03-scenario-${s}`}>
              {s[0].toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <div className="border border-zinc-200 bg-white p-6">
          <div className="overline">Attainable incremental revenue ({scenario})</div>
          <div className="font-serif-display text-4xl mt-2" data-testid="g03-total">{inr(r.total)}</div>
          <div className="mt-4">
            {r.rows.map((row) => (
              <ResultRow key={row.label} label={`${row.label} (${row.adoptionPct}% adoption)`} value={inr(row.value)} />
            ))}
            <ResultRow strong label="After overlap adjustment" value={inr(r.total)} />
          </div>
        </div>
        <div className="mt-4"><Note>Adoption rates are your assumptions — start conservative, pilot the top lever for 90 days, then scale what proves out. Assign each lever an owner before leaving this page.</Note></div>
      </div>
    </div>
  );
}

export function ToolG04() {
  const [v, set] = useNumbers({ baseline: 10000000, target: 20000000 });
  const [drivers, setDrivers] = useState([
    { label: "Existing business organic growth", mode: "pct", value: 8 },
    { label: "New customers", mode: "add", value: 3000000 },
    { label: "Upsell / cross-sell", mode: "add", value: 1500000 },
  ]);
  const r = useMemo(() => revenueBridge({ baseline: v.baseline, target: v.target, drivers: drivers.filter((d) => d.label) }), [v, drivers]);

  const setD = (i, k) => (val) => setDrivers((ds) => ds.map((d, j) => (j === i ? { ...d, [k]: k === "label" || k === "mode" ? val : val } : d)));

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Baseline revenue" prefix="₹" value={v.baseline} onChange={set("baseline")} testid="g04-baseline" />
          <Field label="Target revenue" prefix="₹" value={v.target} onChange={set("target")} testid="g04-target" />
        </div>
        <div className="overline">Growth drivers</div>
        {drivers.map((d, i) => (
          <div key={i} className="border border-zinc-200 bg-white p-3 grid grid-cols-[1fr_86px_120px] gap-2 items-center">
            <input className="text-sm px-2 py-1.5 border border-zinc-200 outline-none focus:border-black" value={d.label} onChange={(e) => setD(i, "label")(e.target.value)} data-testid={`g04-driver-label-${i}`} />
            <select className="text-xs px-2 py-1.5 border border-zinc-200" value={d.mode} onChange={(e) => setD(i, "mode")(e.target.value)} data-testid={`g04-driver-mode-${i}`}>
              <option value="add">₹ add</option>
              <option value="pct">% of running</option>
            </select>
            <input type="number" className="text-sm px-2 py-1.5 border border-zinc-200 outline-none focus:border-black" value={d.value} onChange={(e) => setD(i, "value")(e.target.value)} data-testid={`g04-driver-value-${i}`} />
          </div>
        ))}
        <button type="button" onClick={() => setDrivers((ds) => [...ds, { label: "", mode: "add", value: 0 }])} className="text-sm font-medium underline underline-offset-4" data-testid="g04-add-driver">+ Add driver</button>
        <Note>Percentage drivers apply to the running total — never to the baseline twice. If organic growth already includes an initiative, remove it from one place: double-counted bridges are how targets get missed.</Note>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">The bridge</div>
        <div className="mt-3">
          <ResultRow label="Baseline" value={inr(r.baseline)} />
          {r.steps.map((s, i) => (
            <ResultRow key={i} label={s.label} value={`+${inr(s.delta)} → ${inr(s.to)}`} />
          ))}
          <ResultRow strong label="Projected" value={inr(r.projected)} />
          <ResultRow strong label={r.gap > 0 ? "Gap to target" : "Target exceeded by"} value={inr(Math.abs(r.gap))} tone={r.gap > 0 ? "bad" : "good"} />
          {r.gapClosedPct !== null && <ResultRow label="Gap closed by plan" value={pct(r.gapClosedPct / 100)} />}
        </div>
        {r.gap > 0 && <div className="mt-4"><Note tone="warn">The plan doesn't reach the target. Add drivers, raise assumptions you can defend, or reset the target — in that order of honesty.</Note></div>}
      </div>
    </div>
  );
}

export function ToolG12() {
  const [rows, setRows] = useState([
    { label: "Customer A", value: 50 }, { label: "Customer B", value: 25 },
    { label: "Customer C", value: 15 }, { label: "Others", value: 10 },
  ]);
  const setR = (i, k) => (val) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: val } : r)));
  const r = useMemo(() => concentration(rows.map((x) => x.value)), [rows]);

  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-3">
        <p className="text-sm text-zinc-600">Enter revenue share (%) per customer, SKU, channel or region — anonymised labels are fine.</p>
        {rows.map((row, i) => (
          <div key={i} className="grid grid-cols-[1fr_90px] gap-2">
            <input className="text-sm px-3 py-2 border border-zinc-200 outline-none focus:border-black" value={row.label} onChange={(e) => setR(i, "label")(e.target.value)} data-testid={`g12-label-${i}`} />
            <input type="number" className="text-sm px-3 py-2 border border-zinc-200 outline-none focus:border-black" value={row.value} onChange={(e) => setR(i, "value")(e.target.value)} data-testid={`g12-value-${i}`} />
          </div>
        ))}
        <button type="button" onClick={() => setRows((rs) => [...rs, { label: `Item ${rs.length + 1}`, value: 0 }])} className="text-sm font-medium underline underline-offset-4" data-testid="g12-add">+ Add row</button>
      </div>
      <div className="border border-zinc-200 bg-white p-6 h-fit">
        <div className="overline">Concentration profile</div>
        <div className="mt-3">
          <ResultRow label="Top-1 share" value={pct(r.top1Pct / 100)} tone={r.top1Pct > 40 ? "bad" : undefined} />
          <ResultRow label="Top-3 share" value={pct(r.top3Pct / 100)} tone={r.top3Pct > 70 ? "bad" : undefined} />
          <ResultRow label="HHI (0–10,000)" value={r.hhi.toLocaleString("en-IN")} />
          <ResultRow strong label={`If your largest leaves: −${r.loseTop1.impactPct}%`} value={`${r.loseTop1.revenueLeft}% of revenue left`} tone={r.loseTop1.impactPct > 30 ? "bad" : "good"} />
          <ResultRow strong label={`If your top two leave: −${r.loseTop2.impactPct}%`} value={`${r.loseTop2.revenueLeft}% of revenue left`} tone={r.loseTop2.impactPct > 50 ? "bad" : undefined} />
        </div>
        <div className="mt-4"><Note>HHI above 2,500 is conventionally "concentrated" — but concentration is a risk to manage, not a verdict. Resilience actions: retention programmes for the top accounts, a second channel, and a deliberately built next-ten pipeline.</Note></div>
      </div>
    </div>
  );
}
